import { computed, ref } from 'vue';
import { createI18n } from 'vue-i18n';

import { generateUrl } from '@shop/utils/Routing';

import type { CompileError } from '@intlify/core-base';
import type { ComputedRef } from 'vue';
import type { MessageCompiler } from 'vue-i18n';

const symfonyCompatibleMessageCompiler: MessageCompiler = (message, { onError }) => {
  if (typeof message === 'string') {
    return () => message;
  }
  onError?.(new Error('Locale message AST is not supported in this build') as CompileError);
  return () => '';
};

export function interpolateSymfonyParams(
  text: string,
  parameters?: Record<string, unknown>
): string {
  if (!parameters) {
    return text;
  }

  return Object.entries(parameters).reduce((result, [key, value]) => {
    const token = key.startsWith('{{') ? key : `{{${key}}}`;
    return result.replaceAll(token, String(value));
  }, text);
}

export const lang: string = document.documentElement.getAttribute('lang') ?? 'nl';
export const currency: string = document.documentElement.getAttribute('data-currency') ?? 'EUR';
export const storeCountry: string = (
  document.documentElement.getAttribute('data-locale') ??
  document.body.getAttribute('data-locale') ??
  'nl'
).slice(-2);

const i18n = createI18n({
  locale: lang,
  fallbackLocale: 'nl',
  messages: {},
  missingWarn: false,
  fallbackWarn: false,
  legacy: false,
  messageCompiler: symfonyCompatibleMessageCompiler,
});

const loadedDomains = ref<Set<string>>(new Set());
const loadPromises = new Map<string, Promise<void>>();

async function loadTranslations(domain: string): Promise<void> {
  if (loadedDomains.value.has(domain)) {
    return Promise.resolve();
  }

  const existingPromise = loadPromises.get(domain);
  if (existingPromise) {
    return existingPromise;
  }

  const promise = (async () => {
    const locale = i18n.global.locale.value;

    try {
      const translationUrl = generateUrl('js_translation', {
        domain,
        locales: locale,
        _format: 'json',
      });

      const response = await fetch(translationUrl);

      if (!response.ok) {
        return;
      }

      const data = await response.json();

      if (data.translations?.[locale]?.[domain]) {
        const currentMessages = i18n.global.getLocaleMessage(locale);
        const domainMessages = data.translations[locale][domain];

        i18n.global.mergeLocaleMessage(locale, {
          ...currentMessages,
          ...domainMessages,
        });
        loadedDomains.value.add(domain);
      }
    } catch {
      // No error
    } finally {
      loadPromises.delete(domain);
    }
  })();

  loadPromises.set(domain, promise);
  return promise;
}

const useTranslation = (
  domain = 'messages'
): { t: ComputedRef<(key: string, params?: Record<string, unknown>) => string> } => {
  if (!loadedDomains.value.has(domain)) {
    loadTranslations(domain);
  }

  const t = computed(() => {
    loadedDomains.value.has(domain);
    const translate = i18n.global.t as (key: string, params?: Record<string, unknown>) => string;
    return (key: string, params?: Record<string, unknown>) =>
      interpolateSymfonyParams(translate(key, params ?? {}), params);
  });

  return { t };
};

export default i18n;
export { loadTranslations as loadTranslationDomain, useTranslation };
